'use client';

import { gql, useMutation } from '@apollo/client';
import { useState } from 'react';

const SUBSCRIBE_MUTATION = gql`
  mutation SubscribeEmailToNewsletter($email: String!, $from: Int) {
    subscribeEmailToNewsletter(email: $email, from: $from) {
      status
    }
  }
`;

const EMAIL_REGEX = /^[A-Za-z0-9]+([-_.][A-Za-z0-9]+)*@([A-Za-z0-9]+[-.])+[A-Za-z]{2,5}$/;

export const NewsletterCta = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [subscribe, { loading }] = useMutation(SUBSCRIBE_MUTATION, {
    fetchPolicy: 'no-cache',
    onError: err => {
      if (err.message.toLowerCase().includes('already')) {
        setSubmitted(true);
      } else {
        setError(err.message);
      }
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const email = (new FormData(form).get('email') as string).trim();

    if (!EMAIL_REGEX.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      await subscribe({ variables: { email, from: 3 } });
      setSubmitted(true);
    } catch (e) {
      // error handled by onError
    }
  };

  return (
    <section
      id='newsletter'
      className='relative overflow-hidden px-4 py-24 text-center md_px-6'
      style={{ background: 'linear-gradient(135deg, #eef7f0 0%, #f6f5f3 100%)' }}
    >
      <div
        className='pointer-events-none absolute -right-[10%] -top-[30%] h-[500px] w-[500px] rounded-full'
        style={{ background: 'radial-gradient(circle, rgba(47,97,50,0.06) 0%, transparent 70%)' }}
      />
      <div className='relative z-[1] mx-auto max-w-[1140px]'>
        <h2 className='font-poppins text-[clamp(28px,4vw,44px)] font-extrabold text-[#1a1a1a]'>
          Get Weekly <em className='not-italic text-primary'>Birding Tips</em>
        </h2>
        <p className='mx-auto mb-10 mt-4 max-w-[560px] text-[18px] leading-relaxed text-[#666]'>
          Subscribe to our newsletter for fun facts, feeding guides, and exclusive offers delivered to your inbox.
        </p>

        {submitted ? (
          <div className='mx-auto max-w-[480px] rounded-[10px] border-2 border-primary bg-white px-6 py-8'>
            <h3 className='mb-1 font-poppins text-[20px] font-bold text-[#1a1a1a]'>You&apos;re subscribed!</h3>
            <p className='text-[#666]'>Watch your inbox for weekly birding tips and exclusive offers.</p>
          </div>
        ) : (
          <>
            <form
              onSubmit={onSubmit}
              className='mx-auto flex max-w-[480px] flex-col gap-3 sm_flex-row'
            >
              <input
                type='email'
                name='email'
                required
                placeholder='Enter your email address'
                className='flex-1 rounded-[10px] border-2 border-[#e8e6e2] px-5 py-4 text-base leading-[normal] text-[#000] outline-none transition-colors placeholder_text-[#757575] focus_border-primary'
              />
              <button
                type='submit'
                disabled={loading}
                className='inline-flex items-center justify-center gap-2 rounded-[10px] border-none bg-primary px-10 py-4 font-poppins text-[18px] font-semibold leading-[normal] text-white transition-all hover_-translate-y-0.5 hover_bg-[#245027] disabled_opacity-60'
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {error && <p className='mx-auto mt-3 max-w-[480px] text-center text-sm text-red-600'>{error}</p>}
          </>
        )}
      </div>
    </section>
  );
};

export default NewsletterCta;
